import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import DOMPurify from 'dompurify';
import { getComments, getPresignedUrl } from '@api/comment';
import { CommentType, ImageType } from '@typings/Comment';
import { ApiResponse, ListApiResponse } from '@typings/ApiResponse';
import { capitalizeName } from '@utils/commons';
import Pagination from '@components/pagination';
import ProfileImage from '@components/profile-image.tsx';
import './styles.scss';
import ZoomableImage from '../zoomable-image';
import { addAtribute } from './utils';

interface CommentListProps {
  slug: string | undefined;
  refresh: boolean;
}

const CommentImage: React.FC<{ image: ImageType }> = ({ image }) => {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    getPresignedUrl(image.key).then(setSrc).catch(console.error);
  }, [image.key]);

  if (!src) return null;
  return <ZoomableImage src={src} alt={image.key} />;
};

const CommentList: React.FC<CommentListProps> = ({ slug, refresh }) => {
  const [showSafeguarding, setShowSafeguarding] =
    React.useState<boolean>(false);
  const [commentList, setCommentList] = useState<CommentType[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    const handleGetComments = async () => {
      if (slug) {
        try {
          const response: ApiResponse<ListApiResponse<CommentType>> =
            await getComments(slug, showSafeguarding, currentPage, 3);
          if (response && response.status === 'success') {
            setCommentList(response?.data?.data);
            setTotalPages(response?.data?.pages);
          }
        } catch (error) {
          console.error(error);
        }
      }
    };
    handleGetComments();
  }, [slug, refresh, currentPage, showSafeguarding]);

  useEffect(() => {
    setCurrentPage(1);
  }, [showSafeguarding]);

  const sanitizedContent = (content: string) => {
    const updateContent = addAtribute(content);
    return DOMPurify.sanitize(updateContent, {
      ALLOWED_TAGS: ['a', 'p', 'b', 'i', 'u', 'strong', 'em', 'br'], // Add other tags as needed
      ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'id', 'style'], // Include 'target' explicitly
    });
  };

  return (
    <div className="mt-10">
      <div className="flex justify-end gap-3">
        <div className="govuk-checkboxes__item">
          <input
            className="govuk-checkboxes__input"
            id="viewSafeGuarding"
            name="waste"
            type="checkbox"
            value="carcasses"
            onChange={e => setShowSafeguarding(e.target.checked)}
          />
          <label
            className="govuk-label govuk-checkboxes__label"
            htmlFor="viewSafeGuarding"
          >
            View safeguarding concerns
          </label>
        </div>
      </div>

      <div className="mb-10 govuk-bp:mb-14">
        {commentList?.map(item => (
          <div key={item.id}>
            <div className="flex items-center gap-3">
              <ProfileImage avatar={item?.createByUser?.avatar || ''} />
              <div className="flex-1">
                <h3 className="govuk-heading-s">
                  {capitalizeName(item?.createByUser?.full_name || '')}
                </h3>
                {item.safeguarding_concerns && (
                  <strong className="govuk-tag govuk-tag--red govuk-!-display-inline">
                    Safeguarding concern
                  </strong>
                )}

                <div className="mt-5">
                  {item.createdAt && (
                    <div className="border-none">
                      <p className="govuk-body">
                        {dayjs(item.createdAt).format('h:mm A, DD/MM/YYYY')}
                      </p>
                    </div>
                  )}
                </div>
                <p
                  className="govuk-body"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: sanitizedContent(item?.content),
                  }}
                />

                {item.safeguarding_concerns
                  ? `"${item.safeguarding_concerns}"`
                  : ''}
              </div>
            </div>

            <div className="flex p-3 gap-3 flex-wrap">
              {item.images.map(image => (
                <CommentImage key={image.id} image={image} />
              ))}
            </div>

            <hr className="govuk-section-break govuk-section-break--m govuk-section-break--visible" />
          </div>
        ))}
        <Pagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={totalPages}
        />
      </div>
    </div>
  );
};

export default CommentList;
